import React, { useContext } from 'react';
import {
  FormField,
  Button,
  Box,
  Copy,
  H4,
  Label as DSRLabel,
  Icon,
  Textarea,
  RadioGroup,
  Dropdown as DSRDropdown,
} from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import { uniqueId } from 'lodash';
import themeStyles from './FormElement.style';
import { ModelExplorationContext } from '../../ModelExplorationContext';
import useFormField from '../useFormField';
import { styledCompiler } from '../../../utils/markdown';

const Label = themeStyles.apply(DSRLabel, 'Label');
const Legal = themeStyles.apply(Copy, 'Legal');
const Error = themeStyles.apply(Copy, 'Error');
const TitleHeader = themeStyles.apply(H4, 'Title');
const FormButton = themeStyles.apply(Button, 'FormButton');

const FormFieldWithLabelWrapper = themeStyles.apply(Box, 'FormFieldWithLabelWrapper');

const FormFieldWrapper = ({ children, label, htmlFor, isFieldExtension }) => (
  <FormFieldWithLabelWrapper isFieldExtension={isFieldExtension}>
    <Label htmlFor={htmlFor}>{label}</Label>
    {children}
  </FormFieldWithLabelWrapper>
);

export const SingleLineText = props => {
  const { value, onChange, field, id, name, isValid, enable, label } = useFormField(props);
  return (
    <FormFieldWrapper key={field.model.name} isFieldExtension={enable} htmlFor={field.model.name} label={label}>
      <FormField
        data-testid="cy-sitecore-form-singleline-text"
        hasError={!isValid}
        input={{
          ...field.model,
          id,
          handleInputReset: () => onChange(''),
          name,
          required: field.model.required,
          value,
          onChange,
        }}
      />
    </FormFieldWrapper>
  );
};

export const MultiLineText = props => {
  const { t } = useTranslation();
  const textDisclaimer = t('Shared.Common.formCommentsDisclaimerText');
  const { isDark } = useContext(ModelExplorationContext) || {};
  const { value, onChange, field, id, name, isValid, enable, label } = useFormField(props);
  return (
    <>
      <FormFieldWrapper isFieldExtension={enable} key={field.model.name} htmlFor={field.model.name} label={label}>
        <Textarea
          data-testid="cy-sitecore-form-multiline-text"
          hasError={!isValid}
          id={id}
          name={name}
          required={field.model.required}
          value={value}
          onChange={onChange}
        />
      </FormFieldWrapper>
      {textDisclaimer ? (
        <Legal data-testid="cy-sitecore-form-disclaimer" color={isDark ? 'white' : 'typographyDefault'}>
          {styledCompiler(textDisclaimer)}
        </Legal>
      ) : null}
    </>
  );
};

export const RadioButtonList = props => {
  const { value, onChange, field, id, name, enable, label } = useFormField(props);
  return (
    <FormFieldWrapper isFieldExtension={enable} htmlFor={field.model.name} label={label}>
      <RadioGroup
        key={field.model.name}
        data-testid="cy-sitecore-form-radio-button-list"
        id={id}
        name={name}
        defaultValue={value}
        required={field.model.required}
        radioGroupItemLabelStyles={{ fontSize: '14px !important' }}
        onChangeCallback={onChange}
        options={field.model.items}
        direction="column"
      />
    </FormFieldWrapper>
  );
};

export const Dropdown = props => {
  const { value, onChange, field, id, name, enable, label } = useFormField(props);
  return (
    <FormFieldWrapper isFieldExtension={enable} htmlFor={field.model.name} label={label}>
      <DSRDropdown
        dataTestId="cy-sitecore-form-dropdown"
        options={field.model.items}
        value={value}
        onChange={onChange}
        required={field.model.required}
        id={id}
        name={name}
      />
    </FormFieldWrapper>
  );
};

export const SubmitButton = ({ field, submitInProgress, formIsValid }) => (
  <Box display="flex" justifyContent="center">
    <FormButton data-testid="cy-sitecore-form-button" styling="primary" disabled={!formIsValid || submitInProgress}>
      {submitInProgress ? <Icon name="animatedLoader" /> : styledCompiler(field.model.title)}
    </FormButton>
  </Box>
);

export const Text = ({ field }) => (
  <Box m={0} display="flex" justifyContent="center">
    <TitleHeader data-testid="cy-sitecore-form-text">{field.model.text}</TitleHeader>
  </Box>
);

export const FieldErrorComponent = ({ errors }) => (
  <Box m="l">
    {errors.map(error => (
      <Error data-testid="cy-sitecore-form-field-error" key={uniqueId()}>
        {error}
      </Error>
    ))}
  </Box>
);

export const FormErrorComponent = ({ formErrors }) => (
  <Box m="l">
    {formErrors.map(error => (
      <Error data-testid="cy-sitecore-form-error" key={uniqueId()}>
        {error}
      </Error>
    ))}
  </Box>
);
